/**
 * Módulo de POS de Mostrador de Abarrotes y Venta por Peso.
 *
 * Diseñado para cobro ultra-rápido en mostrador con escáner de código de barras,
 * artículos por pieza o a granel (peso en kg/g) y descuento automático de inventario.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../lib/api';

interface ProductoAbarrote {
  id: number;
  nombre: string;
  codigo_barras: string | null;
  precio: number | string;
  por_peso: number; // 0 o 1
  existencias?: number;
}

interface ItemCarritoAbarrote {
  producto: ProductoAbarrote;
  cantidad: number; // Para productos por pieza
  pesoKg: number;   // Para productos por peso
  subtotal: number;
}

interface ResultadoVenta {
  ordenId: number;
  total: number;
  entregado: number;
  cambio: number;
  detalles: { productoId: number; nombre: string; cantidad: number; subtotal: number }[];
}

export function PosAbarrotes({ sucursalId }: { sucursalId: number }) {
  const [productos, setProductos] = useState<ProductoAbarrote[]>([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');

  const [carrito, setCarrito] = useState<ItemCarritoAbarrote[]>([]);
  const [entregadoInput, setEntregadoInput] = useState('');
  const [procesando, setProcesando] = useState(false);

  // Modal para ingresar peso a granel
  const [prodPesoModal, setProdPesoModal] = useState<ProductoAbarrote | null>(null);
  const [pesoInput, setPesoInput] = useState('0.500');

  // Ticket modal
  const [ventaExitosa, setVentaExitosa] = useState<ResultadoVenta | null>(null);

  const cargarProductos = useCallback(async () => {
    try {
      setCargando(true);
      const res = await api<ProductoAbarrote[]>(`/catalogo?sucursalId=${sucursalId}`);
      setProductos(res);
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }, [sucursalId]);

  useEffect(() => {
    cargarProductos();
  }, [cargarProductos]);

  const agregarAlCarrito = useCallback((prod: ProductoAbarrote, pesoManual?: number) => {
    if (prod.por_peso === 1 && pesoManual === undefined) {
      setProdPesoModal(prod);
      setPesoInput('0.500');
      return;
    }

    setCarrito((prev) => {
      const idx = prev.findIndex((i) => i.producto.id === prod.id);
      const precioUnit = Number(prod.precio);

      if (prod.por_peso === 1) {
        const peso = pesoManual || 0.5;
        const sub = Math.round(precioUnit * peso * 100) / 100;
        if (idx >= 0) {
          const copia = [...prev];
          const nuevoPeso = copia[idx].pesoKg + peso;
          copia[idx] = {
            ...copia[idx],
            pesoKg: nuevoPeso,
            subtotal: Math.round(precioUnit * nuevoPeso * 100) / 100,
          };
          return copia;
        }
        return [...prev, { producto: prod, cantidad: 1, pesoKg: peso, subtotal: sub }];
      }

      // Por pieza
      if (idx >= 0) {
        const copia = [...prev];
        const nuevaCant = copia[idx].cantidad + 1;
        copia[idx] = {
          ...copia[idx],
          cantidad: nuevaCant,
          subtotal: Math.round(precioUnit * nuevaCant * 100) / 100,
        };
        return copia;
      }

      return [...prev, { producto: prod, cantidad: 1, pesoKg: 0, subtotal: precioUnit }];
    });
  }, []);

  // Escáner de Código de Barras USB/Bluetooth
  useEffect(() => {
    let codigoBuffer = '';
    let ultimoTiempo = Date.now();

    function alPresionarTecla(e: KeyboardEvent) {
      // Ignorar si el usuario está escribiendo en el buscador
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

      const ahora = Date.now();
      if (ahora - ultimoTiempo > 100) codigoBuffer = '';
      ultimoTiempo = ahora;

      if (e.key === 'Enter') {
        if (codigoBuffer.length >= 3) {
          const prodEncontrado = productos.find(
            (p) => p.codigo_barras && p.codigo_barras.trim() === codigoBuffer.trim()
          );
          if (prodEncontrado) {
            agregarAlCarrito(prodEncontrado);
          }
        }
        codigoBuffer = '';
      } else if (e.key.length === 1) {
        codigoBuffer += e.key;
      }
    }

    window.addEventListener('keydown', alPresionarTecla);
    return () => window.removeEventListener('keydown', alPresionarTecla);
  }, [productos, agregarAlCarrito]);

  function modificarCantidad(idx: number, delta: number) {
    setCarrito((prev) => {
      const copia = [...prev];
      const item = copia[idx];
      if (item.producto.por_peso === 1) return prev;

      const nuevaCant = item.cantidad + delta;
      if (nuevaCant <= 0) return prev.filter((_, i) => i !== idx);

      const precioUnit = Number(item.producto.precio);
      copia[idx] = {
        ...item,
        cantidad: nuevaCant,
        subtotal: Math.round(precioUnit * nuevaCant * 100) / 100,
      };
      return copia;
    });
  }

  function eliminarItem(idx: number) {
    setCarrito((prev) => prev.filter((_, i) => i !== idx));
  }

  const totalVenta = useMemo(() => carrito.reduce((s, i) => s + i.subtotal, 0), [carrito]);
  const cambio = useMemo(() => {
    const ent = Number(entregadoInput || 0);
    return Math.max(0, ent - totalVenta);
  }, [entregadoInput, totalVenta]);

  const productosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return productos;
    return productos.filter(
      (p) =>
        p.nombre.toLowerCase().includes(q) ||
        (p.codigo_barras && p.codigo_barras.toLowerCase().includes(q))
    );
  }, [productos, busqueda]);

  async function ejecutarCobroVenta() {
    if (carrito.length === 0 || procesando) return;

    const entregadoNum = Number(entregadoInput || totalVenta);
    if (entregadoNum < totalVenta) {
      alert(`El efectivo entregado ($${entregadoNum}) es menor al total ($${totalVenta.toFixed(2)}).`);
      return;
    }

    try {
      setProcesando(true);
      const res = await api<ResultadoVenta>(`/abarrotes/venta?sucursalId=${sucursalId}`, {
        method: 'POST',
        body: {
          items: carrito.map((i) => ({
            productoId: i.producto.id,
            cantidad: i.cantidad,
            pesoKg: i.pesoKg,
          })),
          pago: {
            metodo: 'efectivo',
            entregado: entregadoNum,
          },
        },
      });

      setVentaExitosa(res);
      setCarrito([]);
      setEntregadoInput('');
      await cargarProductos();
    } catch (err: any) {
      alert(err?.message || 'Error al procesar la venta.');
    } finally {
      setProcesando(false);
    }
  }

  if (cargando) {
    return <p className="py-24 text-center text-salvia font-bold">Cargando mostrador de abarrotes…</p>;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 font-cuerpo text-tinta">
      <header className="mb-6 flex items-center justify-between border-b border-salvia/15 pb-4">
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-nopal-oscuro">Punto de Venta</span>
          <h1 className="font-titulo text-2xl font-extrabold text-tinta">POS de Abarrotes</h1>
        </div>

        <div className="text-right">
          <span className="text-xs text-salvia block">Lector de código de barras activo</span>
          <span className="text-xs font-bold text-nopal-oscuro">🟢 Listo para escanear</span>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Columna Izquierda: Catálogo y Buscador */}
        <div className="lg:col-span-7">
          <div className="mb-4">
            <input
              type="text"
              placeholder="🔍 Buscar por nombre o escanear código de barras…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="campo text-base shadow-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {productosFiltrados.map((prod) => (
              <div
                key={prod.id}
                onClick={() => agregarAlCarrito(prod)}
                className="flex flex-col justify-between rounded-2xl border border-salvia/20 bg-white p-3 cursor-pointer transition hover:border-nopal hover:shadow-md"
              >
                <div>
                  <h3 className="font-titulo text-sm font-bold text-tinta line-clamp-2">{prod.nombre}</h3>
                  {prod.por_peso === 1 ? (
                    <span className="inline-block rounded-md bg-miel/20 px-2 py-0.5 text-[10px] font-bold text-miel-oscuro mt-1">
                      ⚖️ Granel / Kg
                    </span>
                  ) : (
                    <span className="inline-block text-[10px] text-salvia mt-1">Pieza</span>
                  )}
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <span className="font-titulo text-base font-extrabold text-nopal-oscuro">
                    ${Number(prod.precio).toFixed(2)}
                  </span>
                  <button className="rounded-lg bg-nopal-claro px-2.5 py-1 text-xs font-bold text-nopal-oscuro">
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Columna Derecha: Carrito de Venta y Cobro */}
        <div className="lg:col-span-5">
          <div className="rounded-3xl border border-salvia/20 bg-white p-5 shadow-md">
            <h2 className="font-titulo text-lg font-bold text-tinta mb-3 border-b border-salvia/15 pb-2">
              Venta de Mostrador ({carrito.length})
            </h2>

            {carrito.length === 0 ? (
              <div className="py-12 text-center text-salvia">
                <p className="text-sm font-semibold">El carrito está vacío.</p>
                <p className="text-xs mt-1">Escanea un código o selecciona del catálogo.</p>
              </div>
            ) : (
              <>
                <div className="max-h-72 overflow-y-auto space-y-2 pr-1 mb-4">
                  {carrito.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between rounded-xl border border-salvia/15 bg-cal/40 p-3">
                      <div>
                        <h4 className="text-xs font-bold text-tinta">{item.producto.nombre}</h4>
                        <span className="text-[10px] text-salvia">
                          {item.producto.por_peso === 1
                            ? `${item.pesoKg} kg x $${Number(item.producto.precio).toFixed(2)}/kg`
                            : `${item.cantidad} x $${Number(item.producto.precio).toFixed(2)}`}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        {item.producto.por_peso === 0 && (
                          <div className="flex items-center rounded-lg border border-salvia/30 bg-white">
                            <button onClick={() => modificarCantidad(idx, -1)} className="px-2 py-0.5 text-xs font-bold text-salvia">
                              -
                            </button>
                            <span className="px-1.5 text-xs font-bold text-tinta">{item.cantidad}</span>
                            <button onClick={() => modificarCantidad(idx, 1)} className="px-2 py-0.5 text-xs font-bold text-salvia">
                              +
                            </button>
                          </div>
                        )}

                        <span className="font-bold text-sm text-nopal-oscuro">${item.subtotal.toFixed(2)}</span>

                        <button onClick={() => eliminarItem(idx)} className="text-xs text-barro font-bold ml-1">
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totales y Cobro */}
                <div className="border-t border-salvia/15 pt-3 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-titulo text-base font-bold text-salvia">TOTAL VENTA</span>
                    <span className="font-titulo text-2xl font-extrabold text-nopal-oscuro">
                      ${totalVenta.toFixed(2)}
                    </span>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-salvia mb-1">Efectivo Entregado ($)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder={totalVenta.toFixed(2)}
                      value={entregadoInput}
                      onChange={(e) => setEntregadoInput(e.target.value)}
                      className="campo text-lg font-bold"
                    />
                  </div>

                  {cambio > 0 && (
                    <div className="rounded-xl border border-nopal/30 bg-nopal-claro p-2.5 text-center">
                      <span className="text-xs font-bold text-nopal-oscuro uppercase">Cambio: </span>
                      <strong className="font-titulo text-xl text-nopal-oscuro">${cambio.toFixed(2)}</strong>
                    </div>
                  )}

                  <button
                    disabled={procesando}
                    onClick={ejecutarCobroVenta}
                    className="w-full btn-principal py-3.5 text-lg font-bold"
                  >
                    {procesando ? 'Cobrando…' : '💳 Confirmar Venta de Mostrador'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Pesaje por Kilos */}
      {prodPesoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-tinta/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
            <h3 className="font-titulo text-lg font-bold text-tinta mb-1">{prodPesoModal.nombre}</h3>
            <p className="text-xs text-salvia mb-4">Precio por Kg: ${Number(prodPesoModal.precio).toFixed(2)}</p>

            <div className="mb-4">
              <label className="block text-xs font-bold text-salvia mb-1">Peso en Kilogramos (kg) *</label>
              <input
                type="number"
                step="0.001"
                min="0.001"
                value={pesoInput}
                onChange={(e) => setPesoInput(e.target.value)}
                className="campo text-xl font-bold text-nopal-oscuro"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setProdPesoModal(null)}
                className="flex-1 rounded-xl border border-salvia/30 py-2.5 text-sm font-bold text-salvia"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  const peso = Number(pesoInput) || 0.5;
                  agregarAlCarrito(prodPesoModal, peso);
                  setProdPesoModal(null);
                }}
                className="flex-1 btn-principal py-2.5 text-sm font-bold"
              >
                Agregar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Ticket de Venta Exitosa */}
      {ventaExitosa && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-tinta/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl text-center">
            <span className="text-4xl">✅</span>
            <h3 className="font-titulo text-xl font-bold text-tinta mt-2">Venta Cobrada Exitosamente</h3>
            <p className="text-xs text-salvia mt-1">Ticket #{ventaExitosa.ordenId}</p>

            <div className="my-4 rounded-2xl bg-nopal-claro p-4 text-nopal-oscuro font-bold text-2xl font-titulo">
              Total: ${ventaExitosa.total.toFixed(2)}
            </div>

            {ventaExitosa.cambio > 0 && (
              <p className="text-sm font-bold text-tinta mb-4">
                Cambio Entregado: ${ventaExitosa.cambio.toFixed(2)}
              </p>
            )}

            <button
              onClick={() => setVentaExitosa(null)}
              className="w-full btn-principal py-3 font-bold"
            >
              Siguiente Venta
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
